import { useCallback, useState } from "react";

export type ViewState = { view: "users-by-subject"; subjectUserId: number };

export interface BreadcrumbEntry {
  label: string;
  showPersonnelIcon: boolean;
  viewState: ViewState;
}

export interface NavState {
  current: ViewState;
  breadcrumbs: BreadcrumbEntry[];
}

export function getViewKey(state: ViewState): string {
  return `subject-${state.subjectUserId}`;
}

export function useMyTeamsNav(callerUserId: number) {
  const [state, setState] = useState<NavState>(() => ({
    current: { view: "users-by-subject", subjectUserId: callerUserId },
    breadcrumbs: [
      {
        label: "My Team",
        showPersonnelIcon: true,
        viewState: { view: "users-by-subject", subjectUserId: callerUserId },
      },
    ],
  }));

  const navigateToPerson = useCallback(
    (userId: number, displayName: string) => {
      setState((prev) => {
        const existingIndex = prev.breadcrumbs.findIndex(
          (b) => b.viewState.subjectUserId === userId
        );
        if (existingIndex !== -1) {
          return {
            current: prev.breadcrumbs[existingIndex].viewState,
            breadcrumbs: prev.breadcrumbs.slice(0, existingIndex + 1),
          };
        }
        const newViewState: ViewState = {
          view: "users-by-subject",
          subjectUserId: userId,
        };
        return {
          current: newViewState,
          breadcrumbs: [
            ...prev.breadcrumbs,
            {
              label: displayName,
              showPersonnelIcon: false,
              viewState: newViewState,
            },
          ],
        };
      });
    },
    []
  );

  const navigateToBreadcrumb = useCallback((index: number) => {
    setState((prev) => {
      const entry = prev.breadcrumbs[index];
      if (!entry) return prev;
      return {
        current: entry.viewState,
        breadcrumbs: prev.breadcrumbs.slice(0, index + 1),
      };
    });
  }, []);

  return {
    ...state,
    navigateToPerson,
    navigateToBreadcrumb,
  };
}
